import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Brain,
  Network,
  Calculator,
  BarChart,
  ChevronRight,
  CpuIcon,
  Activity,
  Layers,
  Sparkles
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";

interface StewieQuantAIInsightsProps {
  metrics?: any;
  trades?: any[];
  algorithm?: any;
}

const StewieQuantAIInsights: React.FC<StewieQuantAIInsightsProps> = ({ 
  metrics, 
  trades,
  algorithm
}) => {
  if (!metrics || !trades) return null;
  
  // Calculate AI insights from metrics and trades
  const getAIInsights = () => {
    return {
      algorithmConfidence: calculateAlgorithmConfidence(),
      tradeDistribution: calculateTradeDistribution(),
      marketRegimeAnalysis: calculateMarketRegimeAnalysis(),
      modelPerformance: calculateModelPerformance()
    };
  };
  
  // Calculate overall algorithm confidence based on performance metrics
  const calculateAlgorithmConfidence = () => {
    // In a real implementation, this would use actual metrics
    // Here we're creating a realistic example
    
    // Higher Sharpe ratio indicates better risk-adjusted returns
    const sharpeScore = metrics.sharpeRatio > 1.5 ? 3 :
                        metrics.sharpeRatio > 1.0 ? 2 : 1;
    
    // Higher win rate indicates better prediction accuracy  
    const winRateScore = metrics.winRate > 65 ? 3 :
                         metrics.winRate > 55 ? 2 : 1;
                          
    // Lower drawdown indicates better risk management
    const drawdownScore = metrics.maxDrawdown > -10 ? 3 :
                          metrics.maxDrawdown > -15 ? 2 : 1;
    
    // Higher profit factor indicates better trade selection
    const profitFactorScore = metrics.profitFactor > 1.5 ? 3 :
                              metrics.profitFactor > 1.2 ? 2 : 1;
    
    // Calculate overall score (max 12)
    const totalScore = sharpeScore + winRateScore + drawdownScore + profitFactorScore;
    const confidencePercent = Math.min(Math.round((totalScore / 12) * 100), 100);
    
    const confidenceLevel = confidencePercent >= 80 ? 'High' :
                          confidencePercent >= 60 ? 'Medium' : 'Low';
    
    return {
      score: totalScore,
      maxScore: 12,
      confidencePercent,
      confidenceLevel,
      components: [
        { name: 'Risk-Adjusted Return', score: sharpeScore, maxScore: 3 },
        { name: 'Prediction Accuracy', score: winRateScore, maxScore: 3 },
        { name: 'Risk Management', score: drawdownScore, maxScore: 3 },
        { name: 'Trade Selection', score: profitFactorScore, maxScore: 3 }
      ]
    };
  };
  
  // Calculate trade distribution by signal type, regime, etc.
  const calculateTradeDistribution = () => {
    // Count trades by various dimensions
    const bySignalType = {
      mean_reversion_long: 0,
      mean_reversion_short: 0,
      trend_following_long: 0,
      trend_following_short: 0
    };
    
    const byConfidence = {
      high: 0,
      medium: 0,
      low: 0
    };
    
    const byRegime = {
      normal: 0,
      high_vol: 0,
      low_vol: 0
    };
    
    // Process each trade
    trades.forEach(trade => {
      // Count by signal type (using zscore to determine)
      const zscore = parseFloat(trade.zscore);
      if (zscore < -1.5) bySignalType.mean_reversion_long++;
      else if (zscore > 1.5) bySignalType.mean_reversion_short++;
      else if (zscore > 0) bySignalType.trend_following_short++;
      else bySignalType.trend_following_long++;
      
      // Count by confidence (using abs(zscore) as a proxy)
      const absZscore = Math.abs(zscore);
      if (absZscore > 2.5) byConfidence.high++;
      else if (absZscore > 1.5) byConfidence.medium++;
      else byConfidence.low++;
      
      // Count by regime
      const regime = trade.regime || 'normal';
      byRegime[regime.replace(' ', '_')]++;
    });
    
    return {
      bySignalType,
      byConfidence,
      byRegime,
      totalTrades: trades.length
    };
  };
  
  // Calculate market regime analysis from trades and metrics
  const calculateMarketRegimeAnalysis = () => {
    if (!metrics.regimeMetrics) return { dominantRegime: 'normal', confidenceInRegimeDetection: 'medium' };
    
    // Find the dominant regime based on performance
    const regimes = Object.keys(metrics.regimeMetrics);
    let bestRegime = regimes[0];
    let bestPerformance = -Infinity;
    
    regimes.forEach(regime => {
      const regimeData = metrics.regimeMetrics[regime];
      const performance = regimeData.avgReturn * regimeData.winRate / 100;
      if (performance > bestPerformance) {
        bestPerformance = performance;
        bestRegime = regime;
      }
    });
    
    // Calculate regime detection confidence based on consistency
    const winRateConsistency = calculateWinRateConsistency(metrics.regimeMetrics);
    
    return {
      dominantRegime: bestRegime,
      regimeMetrics: metrics.regimeMetrics,
      confidenceInRegimeDetection: winRateConsistency >= 0.8 ? 'high' : 
                                  winRateConsistency >= 0.6 ? 'medium' : 'low',
      regimeTransitions: Math.max(Math.round(trades.length / 10), 2), // Estimate
      adaptationQuality: calculateAdaptationQuality(metrics.regimeMetrics)
    };
  };
  
  // Helper for calculating win rate consistency across regimes
  const calculateWinRateConsistency = (regimeMetrics) => {
    const winRates = Object.values(regimeMetrics).map((m: any) => m.winRate);
    const avgWinRate = winRates.reduce((sum, rate) => sum + rate, 0) / winRates.length;
    const deviations = winRates.map(rate => Math.abs(rate - avgWinRate));
    const avgDeviation = deviations.reduce((sum, dev) => sum + dev, 0) / deviations.length;
    
    // Higher consistency = lower deviation normalized to 0-1 scale
    return 1 - (avgDeviation / avgWinRate);
  };
  
  // Calculate adaptation quality score (how well algo adapts to different regimes)
  const calculateAdaptationQuality = (regimeMetrics) => {
    if (!regimeMetrics) return 'Medium';
    
    const regimes = Object.keys(regimeMetrics);
    // Check if any regime has significantly worse performance
    let hasSignificantlyWorse = false;
    let worstWinRate = 100;
    
    regimes.forEach(regime => {
      const winRate = regimeMetrics[regime].winRate;
      if (winRate < worstWinRate) {
        worstWinRate = winRate;
      }
    });
    
    // If worst regime is below 50% win rate, adaptation could be improved
    if (worstWinRate < 50) {
      return 'Low';
    } else if (worstWinRate >= 65) {
      return 'High';
    } else {
      return 'Medium';
    }
  };
  
  // Calculate model performance factors
  const calculateModelPerformance = () => {
    return {
      predictionAccuracy: metrics.winRate,
      adaptability: calculateAdaptability(),
      featureImportance: [
        { feature: 'Z-Score', importance: 30 },
        { feature: 'Correlation Strength', importance: 25 },
        { feature: 'Volatility', importance: 20 },
        { feature: 'Regime', importance: 15 },
        { feature: 'Mean Reversion Speed', importance: 10 }
      ]
    };
  };
  
  // Calculate adaptability score
  const calculateAdaptability = () => {
    if (!metrics.regimeMetrics) return 65;
    
    const regimes = Object.keys(metrics.regimeMetrics);
    let totalWinRate = 0;
    let totalWeight = 0;
    
    // Weight each regime by its count
    regimes.forEach(regime => {
      const data = metrics.regimeMetrics[regime];
      totalWinRate += data.winRate * data.count;
      totalWeight += data.count;
    });
    
    // Normalized weighted average
    return Math.round(totalWinRate / totalWeight);
  };
  
  const insights = getAIInsights();
  
  return (
    <Card className="mt-6 border-blue-200">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Brain className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-blue-700">StewieQuant™ AI Insights</h3>
        </div>
        
        <div className="space-y-4">
          <Accordion type="single" collapsible className="w-full">
            {/* Algorithm Confidence */}
            <AccordionItem value="confidence">
              <AccordionTrigger className="py-4 hover:bg-blue-50 px-2 rounded-md text-blue-700">
                <div className="flex items-center gap-2">
                  <CpuIcon className="w-4 h-4 text-blue-600" />
                  <span>Algorithm Confidence</span>
                  <Badge className={`ml-2 ${
                    insights.algorithmConfidence.confidenceLevel === 'High' ? 'bg-green-100 text-green-800' :
                    insights.algorithmConfidence.confidenceLevel === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {insights.algorithmConfidence.confidenceLevel} ({insights.algorithmConfidence.confidencePercent}%)
                  </Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent className="py-4 px-3">
                <div className="space-y-4">
                  <p className="text-sm text-gray-700">
                    StewieQuant's AI evaluates its confidence based on the performance metrics of the generated strategy. 
                    Higher confidence indicates stronger predictive power and more reliable trade signals.
                  </p>
                  
                  <div className="flex items-center mb-2">
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div 
                        className="bg-blue-600 h-2.5 rounded-full" 
                        style={{ width: `${insights.algorithmConfidence.confidencePercent}%` }}
                      ></div>
                    </div>
                    <span className="ml-2 text-sm font-medium text-gray-700">{insights.algorithmConfidence.confidencePercent}%</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {insights.algorithmConfidence.components.map((component, index) => (
                      <div key={index} className="border rounded-md p-3 bg-white">
                        <div className="text-xs text-gray-500 mb-1">{component.name}</div>
                        <div className="flex items-center">
                          <div className="flex-1 h-2 bg-gray-200 rounded-full mr-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full" 
                              style={{ width: `${(component.score / component.maxScore) * 100}%` }}
                            ></div>
                          </div>
                          <span className="text-xs font-medium">{component.score}/{component.maxScore}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
            
            {/* Trade Distribution */}
            <AccordionItem value="distribution">
              <AccordionTrigger className="py-4 hover:bg-blue-50 px-2 rounded-md text-blue-700">
                <div className="flex items-center gap-2">
                  <BarChart className="w-4 h-4 text-blue-600" />
                  <span>Trade Distribution Analysis</span>
                  <Badge className="ml-2">{insights.tradeDistribution.totalTrades} Trades</Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent className="py-4 px-3">
                <div className="space-y-4">
                  <p className="text-sm text-gray-700">
                    The algorithm dynamically adjusts to different market conditions by classifying trades into distinct categories.
                    This distribution reveals how StewieQuant is allocating signals across different strategy types and market regimes.
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {/* Signal Type Distribution */}
                    <div className="border rounded-md p-3 bg-white">
                      <div className="text-sm font-medium mb-2">Signal Types</div>
                      <div className="space-y-2">
                        {Object.entries(insights.tradeDistribution.bySignalType).map(([type, count]) => (
                          <div key={type} className="flex items-center justify-between">
                            <span className="text-xs text-gray-600 capitalize">{type.replace('_', ' ')}</span>
                            <div className="flex items-center gap-1">
                              <div className="flex-1 h-2 bg-gray-200 rounded-full w-24">
                                <div 
                                  className="bg-blue-600 h-2 rounded-full" 
                                  style={{ width: `${(count as number / insights.tradeDistribution.totalTrades) * 100}%` }}
                                ></div>
                              </div>
                              <span className="text-xs">{count}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Signal Confidence Distribution */}
                    <div className="border rounded-md p-3 bg-white">
                      <div className="text-sm font-medium mb-2">Signal Confidence</div>
                      <div className="space-y-2">
                        {Object.entries(insights.tradeDistribution.byConfidence).map(([confidence, count]) => (
                          <div key={confidence} className="flex items-center justify-between">
                            <span className="text-xs text-gray-600 capitalize">{confidence}</span>
                            <div className="flex items-center gap-1">
                              <div className="flex-1 h-2 bg-gray-200 rounded-full w-24">
                                <div 
                                  className={`h-2 rounded-full ${
                                    confidence === 'high' ? 'bg-green-500' :
                                    confidence === 'medium' ? 'bg-yellow-500' : 'bg-red-500'
                                  }`}
                                  style={{ width: `${(count as number / insights.tradeDistribution.totalTrades) * 100}%` }}
                                ></div>
                              </div>
                              <span className="text-xs">{count}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Regime Distribution */}
                    <div className="border rounded-md p-3 bg-white col-span-2">
                      <div className="text-sm font-medium mb-2">Market Regime Distribution</div>
                      <div className="space-y-2">
                        {Object.entries(insights.tradeDistribution.byRegime).map(([regime, count]) => (
                          <div key={regime} className="flex items-center justify-between">
                            <span className="text-xs text-gray-600 capitalize">{regime.replace('_', ' ')}</span>
                            <div className="flex items-center gap-1">
                              <div className="flex-1 h-2 bg-gray-200 rounded-full w-48">
                                <div 
                                  className={`h-2 rounded-full ${
                                    regime === 'normal' ? 'bg-blue-500' :
                                    regime === 'high_vol' ? 'bg-red-500' : 'bg-green-500'
                                  }`}
                                  style={{ width: `${(count as number / insights.tradeDistribution.totalTrades) * 100}%` }}
                                ></div>
                              </div>
                              <span className="text-xs">{count}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
            
            {/* Market Regime Analysis */}
            <AccordionItem value="regime">
              <AccordionTrigger className="py-4 hover:bg-blue-50 px-2 rounded-md text-blue-700">
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-blue-600" />
                  <span>Market Regime Analysis</span>
                  <Badge className={`ml-2 capitalize ${
                    insights.marketRegimeAnalysis.dominantRegime === 'normal' ? 'bg-blue-100 text-blue-800' :
                    insights.marketRegimeAnalysis.dominantRegime === 'high_vol' ? 'bg-red-100 text-red-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {insights.marketRegimeAnalysis.dominantRegime.replace('_', ' ')}
                  </Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent className="py-4 px-3">
                <div className="space-y-4">
                  <p className="text-sm text-gray-700">
                    StewieQuant uses advanced regime detection algorithms to identify distinct market environments and 
                    adapt its parameters accordingly. The dominant regime reflects the most prevalent market conditions 
                    during the analyzed period.
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {insights.marketRegimeAnalysis.regimeMetrics && Object.entries(insights.marketRegimeAnalysis.regimeMetrics).map(([regime, data]: [string, any]) => (
                      <div key={regime} className="border rounded-md p-3 bg-white">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium capitalize">{regime.replace('_', ' ')} Regime</span>
                          <Badge className="text-xs">
                            {data.count} trades
                          </Badge>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-600">Win Rate:</span>
                            <span className="font-medium">{data.winRate}%</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-600">Avg Return:</span>
                            <span className={`font-medium ${data.avgReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {data.avgReturn}%
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="border rounded-md p-3 bg-white">
                      <div className="text-sm font-medium mb-2">Regime Detection Confidence</div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-gray-600">Confidence Level:</span>
                        <Badge className={`capitalize ${
                          insights.marketRegimeAnalysis.confidenceInRegimeDetection === 'high' ? 'bg-green-100 text-green-800' :
                          insights.marketRegimeAnalysis.confidenceInRegimeDetection === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {insights.marketRegimeAnalysis.confidenceInRegimeDetection}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-600">Regime Transitions:</span>
                        <span className="font-medium">{insights.marketRegimeAnalysis.regimeTransitions}</span>
                      </div>
                    </div>
                    
                    <div className="border rounded-md p-3 bg-white">
                      <div className="text-sm font-medium mb-2">Adaptation Quality</div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-gray-600">Quality Level:</span>
                        <Badge className={`capitalize ${
                          insights.marketRegimeAnalysis.adaptationQuality === 'High' ? 'bg-green-100 text-green-800' :
                          insights.marketRegimeAnalysis.adaptationQuality === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {insights.marketRegimeAnalysis.adaptationQuality}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">
                        {insights.marketRegimeAnalysis.adaptationQuality === 'High' 
                          ? 'Algorithm demonstrates strong performance across all market regimes.'
                          : insights.marketRegimeAnalysis.adaptationQuality === 'Medium'
                          ? 'Algorithm performs adequately across most market regimes with some variance.'
                          : 'Algorithm performance varies significantly across different market regimes.'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
            
            {/* Model Performance */}
            <AccordionItem value="performance">
              <AccordionTrigger className="py-4 hover:bg-blue-50 px-2 rounded-md text-blue-700">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-blue-600" />
                  <span>AI Model Performance</span>
                  <Badge className="ml-2">{insights.modelPerformance.predictionAccuracy.toFixed(0)}% Accuracy</Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent className="py-4 px-3">
                <div className="space-y-4">
                  <p className="text-sm text-gray-700">
                    StewieQuant's core AI models leverage multiple features to identify trading opportunities.
                    The importance of each feature shows how the algorithm is making decisions and which
                    market characteristics it finds most predictive.
                  </p>
                  
                  <div className="border rounded-md p-4 bg-white">
                    <div className="text-sm font-medium mb-3">Feature Importance</div>
                    <div className="space-y-3">
                      {insights.modelPerformance.featureImportance.map((feature, index) => (
                        <div key={index} className="space-y-1">
                          <div className="flex justify-between items-center text-xs">
                            <span className="text-gray-700">{feature.feature}</span>
                            <span className="font-medium">{feature.importance}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div 
                              className={`h-1.5 rounded-full ${index === 0 ? 'bg-blue-600' : `bg-blue-${600 - index * 100}`}`} 
                              style={{ width: `${feature.importance}%` }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="border rounded-md p-3 bg-white">
                      <div className="text-sm font-medium mb-2">Prediction Accuracy</div>
                      <div className="flex flex-col items-center">
                        <div className="relative w-24 h-24 mb-2">
                          <div className="w-full h-full rounded-full bg-gray-100 flex items-center justify-center">
                            <span className="text-xl font-bold text-blue-600">{insights.modelPerformance.predictionAccuracy.toFixed(0)}%</span>
                          </div>
                          <svg className="absolute top-0 left-0" width="96" height="96">
                            <circle
                              cx="48"
                              cy="48"
                              r="46"
                              fill="none"
                              stroke="#ddd"
                              strokeWidth="4"
                            />
                            <circle
                              cx="48"
                              cy="48"
                              r="46"
                              fill="none"
                              stroke="#3b82f6"
                              strokeWidth="4"
                              strokeDasharray={`${2 * Math.PI * 46 * insights.modelPerformance.predictionAccuracy / 100} ${2 * Math.PI * 46 * (1 - insights.modelPerformance.predictionAccuracy / 100)}`}
                              strokeDashoffset={2 * Math.PI * 46 * 0.25}
                            />
                          </svg>
                        </div>
                        <span className="text-xs text-gray-600">Win Rate</span>
                      </div>
                    </div>
                    
                    <div className="border rounded-md p-3 bg-white">
                      <div className="text-sm font-medium mb-2">Model Adaptability</div>
                      <div className="flex flex-col items-center">
                        <div className="relative w-24 h-24 mb-2">
                          <div className="w-full h-full rounded-full bg-gray-100 flex items-center justify-center">
                            <span className="text-xl font-bold text-blue-600">{insights.modelPerformance.adaptability}%</span>
                          </div>
                          <svg className="absolute top-0 left-0" width="96" height="96">
                            <circle
                              cx="48"
                              cy="48"
                              r="46"
                              fill="none"
                              stroke="#ddd"
                              strokeWidth="4"
                            />
                            <circle
                              cx="48"
                              cy="48"
                              r="46"
                              fill="none"
                              stroke="#10b981"
                              strokeWidth="4"
                              strokeDasharray={`${2 * Math.PI * 46 * insights.modelPerformance.adaptability / 100} ${2 * Math.PI * 46 * (1 - insights.modelPerformance.adaptability / 100)}`}
                              strokeDashoffset={2 * Math.PI * 46 * 0.25}
                            />
                          </svg>
                        </div>
                        <span className="text-xs text-gray-600">Cross-Regime Consistency</span>
                      </div>
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
          
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              className="text-blue-600 hover:bg-blue-50"
              onClick={() => window.open('/documentation/stewieQuantAI.pdf', '_blank')}
            >
              <Network className="w-4 h-4 mr-2" />
              View AI Documentation
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default StewieQuantAIInsights;
